import { Button } from "@components/ui/button";
import { CreateProfileContext } from "@pages/CreateProfile/CreateProfileLayout";
import { RootState } from "@store/reducers";
import { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Cropper, ReactCropperElement } from "react-cropper";
// @ts-ignore
import "cropperjs/dist/cropper.css";
import { useSnackbar } from "notistack";
// @ts-ignore
import { profileImage, uploadUrl } from "../../../../endpoints/images.service";
// @ts-ignore
import { updateCandidate } from "../../../../endpoints/candidate.service";
// @ts-ignore
import { setUserData } from "../../../../store/actions/auth.action";
// @ts-ignore
import LoadingAnimation from "../../../../assets/svg/LoadingAnimation";
import { colors } from "../../../../utils/theme";

const Step2 = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state: RootState) => state.auth);
  const { setActiveStep, setSteps } = useContext(CreateProfileContext)!;

  const filePickerRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { watch, setValue } = useForm<{
    file?: File;
    image?: string;
    profileImage?: string;
    profilePic?: string;
  }>({
    defaultValues: {
      profilePic: user?.profilePic,
    },
  });

  const fields = watch();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      setValue("file", file, { shouldDirty: true });
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue("image", reader.result as string, { shouldDirty: true });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error during file upload:", error);
    }
  };

  const getCroppedImage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const cropperInstance = cropperRef.current?.cropper;
    if (!cropperInstance) {
      console.error("Cropper instance is not found");
      return;
    }
    const croppedDataUrl = cropperInstance.getCroppedCanvas().toDataURL();
    setValue("profileImage", croppedDataUrl);
    setValue("image", undefined);
  };

  const handleSaveAndNext = async () => {
    if (!fields?.profileImage) {
      setActiveStep((prev) => prev + 1);
      setSteps((prev) =>
        prev.map((step, i) => (i === 1 ? { ...step, isCompleted: true } : step)),
      );
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await profileImage();
      if (!data?.data?.uploadURL) throw new Error("Failed to get upload URL");

      const base64Data = fields.profileImage.split(";base64,").pop()!;
      const byteArray = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: fields.file?.type });

      const formData = new FormData();
      formData.append("file", blob, fields.file?.name);

      const fileResponse = await uploadUrl(data.data.uploadURL, formData);
      const picUrl = fileResponse?.variants?.find(
        (x: string) => x.split("/").slice(-1)[0] === "public",
      );

      const { success, data: userApiData } = await updateCandidate({
        profilePic: picUrl,
      });
      if (success) {
        dispatch(setUserData(userApiData));
        setValue("profileImage", undefined);
        setValue("image", undefined);
        setActiveStep((prev) => prev + 1);
        setSteps((prev) =>
          prev.map((step, i) => (i === 1 ? { ...step, isCompleted: true } : step)),
        );
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Unable to upload image.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const displayPic = fields?.profileImage || fields?.profilePic || user?.profilePic;

  return (
    <div className="flex flex-col bg-white rounded-md p-5 w-[40%] h-max min-h-96">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="font-bold text-primary">Profile photo</span>
          <span className="text-sm text-light-black">
            Add your profile image.
          </span>
        </div>
        <span className="rounded-full font-semibold text-xss text-blue-600 bg-blue-50 px-2 py-0.5">
          Step 2
        </span>
      </div>

      {/* Image picker / Cropper container */}
      <div className="flex flex-col justify-center items-center w-full mt-4 gap-4 border rounded-md p-5">
        {fields?.image ? (
          <>
            <Cropper
              src={fields.image}
              ref={cropperRef}
              style={{ height: 320, width: "100%" }}
              aspectRatio={1}
              viewMode={2}
              guides={false}
              minCropBoxWidth={50}
              minCropBoxHeight={50}
            />
            <style>{`
              .cropper-face,
              .cropper-view-box,
              .cropper-crop-box {
                border-radius: 50% !important;
                border: 2px solid white !important;
              }
              .cropper-face {
                background-color: rgba(0, 0, 0, 0.3) !important;
              }
            `}</style>
            <button
              type="button"
              onClick={getCroppedImage}
              className="rounded-md bg-primary text-white h-9 px-4 font-medium text-xs"
            >
              Crop Image
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center h-28 w-28 rounded-full border bg-[#E2E8F0] overflow-hidden">
              {displayPic ? (
                <img src={displayPic} className="h-full w-full object-cover" />
              ) : (
                <span className="font-bold text-3xl text-primary">
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => filePickerRef.current?.click()}
              className="flex items-center border rounded-lg overflow-hidden w-full"
            >
              <span className="text-xs text-[#6f6f6f] font-medium py-2.5 px-2 bg-[#E9EAF0]">
                PNG/JPG
              </span>
              <span className="text-xs flex font-medium px-3 text-ngray flex-1 text-left whitespace-nowrap">
                {fields?.file?.name || "Upload your profile picture"}
              </span>
            </button>
            <input
              ref={filePickerRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleFileChange}
            />
          </>
        )}
      </div>

      <div className="flex items-center gap-2 w-full mt-4">
        <button
          onClick={() => setActiveStep((prev) => prev - 1)}
          className="rounded-md bg-white border border-primary text-primary h-9 px-3 font-medium text-xs hover:bg-light-white-400"
        >
          Back
        </button>
        <Button
          onClick={handleSaveAndNext}
          disabled={isLoading || !!fields?.image}
          variant="secondary"
          className="flex-1 flex items-center justify-center gap-2"
        >
          {isLoading && <LoadingAnimation color={colors.White} />}
          Next - add career details
        </Button>
      </div>
    </div>
  );
};

export default Step2;
